/* eslint-disable @typescript-eslint/ban-types */
import QueryTags from '../types/QueryTags'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'

const basePath = '/orders'

const OrderEndpoints = (builder: EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, QueryTags, 'data'>) =>
  ({
    fetchOrders: builder.query<any, any>({
      query: (params) => ({
        url: `${basePath}`,
        params,
      }),
      providesTags: ['orders']
    }),

    fetchOrderById: builder.query<any, string>({
      query: (id) => `${basePath}/${id}`,
      providesTags: ['orders']
    }),

    createOrder: builder.mutation<any, any>({
      query: (body) => ({
        url: `${basePath}`,
        method: 'POST',
        body,
      }),
      // a transport amount on the order is recorded as an expense
      invalidatesTags: ['orders', 'expenses']
    }),

    // transport paid for the customer's clothes: an expense linked to the order, not part of its bill
    addOrderTransport: builder.mutation<any, { id: string; body: { amount: number; note?: string } }>({
      query: ({ id, body }) => ({
        url: `${basePath}/${id}/transport`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['orders', 'expenses']
    }),

    fetchOrderTransport: builder.query<any[], string>({
      query: (id) => `${basePath}/${id}/transport`,
      providesTags: ['orders', 'expenses']
    }),

    updateOrderStatus: builder.mutation<any, { id: string; body: { status: string } }>({
      query: ({ id, body }) => ({
        url: `${basePath}/${id}/status`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['orders']
    }),

    makePayment: builder.mutation<any, { id: string; body: { amount: number } }>({
      query: ({ id, body }) => ({
        url: `${basePath}/${id}/payment`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['orders']
    }),

    // the SMS for the order's current status, fetched fresh each time staff open the preview
    fetchOrderSmsPreview: builder.query<{ phoneNumber: string; message: string }, string>({
      query: (id) => `${basePath}/${id}/sms`,
      keepUnusedDataFor: 0,
    }),

    sendOrderSms: builder.mutation<any, { id: string; message: string }>({
      query: ({ id, message }) => ({
        url: `${basePath}/${id}/sms`,
        method: 'POST',
        body: { message },
      }),
    }),

    deleteOrder: builder.mutation<any, string>({
      query: (id) => ({
        url: `${basePath}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['orders']
    }),

    fetchOrdersSummary: builder.query<any, { startDate?: string; endDate?: string } | void>({
      query: (params) => ({
        url: `${basePath}/summary`,
        params: params || undefined,
      }),
      providesTags: ['orders']
    }),

    fetchOrderReport: builder.query<any, { startDate: string; endDate: string }>({
      query: (params) => ({
        url: `${basePath}/order-report`,
        params,
      }),
      providesTags: ['orders']
    }),

    fetchOrderCustomerReport: builder.query<any, void>({
      query: () => `${basePath}/customer-report`,
      providesTags: ['orders']
    }),

  })

export default OrderEndpoints
